import React from 'react';
import './NotificationTimestampAtom.scss';

export interface NotificationTimestampAtomProps {
  timestamp: Date | string;
  format?: 'relative' | 'absolute';
  className?: string;
}

/**
 * NotificationTimestampAtom - Displays relative time ("2h ago") with tooltip showing full date
 * Based on SphereChatPanel's formatDate function (lines 183-214)
 */
const NotificationTimestampAtom: React.FC<NotificationTimestampAtomProps> = ({
  timestamp,
  format = 'relative',
  className = ''
}) => {
  const formatDate = (dateString: Date | string): string => {
    const messageDate = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - messageDate.getTime());
    const diffMins = Math.floor(diffTime / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const messageDay = messageDate.toDateString();
    const todayDay = today.toDateString();
    const yesterdayDay = yesterday.toDateString();

    const timeString = messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    if (format === 'absolute') {
      if (messageDay === todayDay) {
        return `Today ${timeString}`;
      } else if (messageDay === yesterdayDay) {
        return `Yesterday ${timeString}`;
      } else if (diffDays <= 7) {
        const dayName = messageDate.toLocaleDateString([], { weekday: 'long' });
        return `${dayName} ${timeString}`;
      } else if (diffDays <= 365) {
        const monthDay = messageDate.toLocaleDateString([], { month: 'short', day: 'numeric' });
        return `${monthDay} ${timeString}`;
      } else {
        const fullDate = messageDate.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' });
        return `${fullDate} ${timeString}`;
      }
    }

    // Relative format (default)
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;

    return messageDate.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const getFullDateString = (): string => {
    const messageDate = new Date(timestamp);
    return messageDate.toLocaleString([], {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <span
      className={`notification-timestamp-atom ${className}`}
      title={getFullDateString()}
    >
      {formatDate(timestamp)}
    </span>
  );
};

export default NotificationTimestampAtom;